
import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  DataGrid,
  GridToolbar,
} from "@mui/x-data-grid";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

import { procRateList } from "../../../services/reportService";
import { generateReportPDF } from "../../../utils/pdfReport";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";

function RateList() {
  const [rateData, setRateData] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================================================
  // LOAD RATE LIST
  // =========================================================
  const rptRateList = async () => {
    try {
      setLoading(true);

      const res = await procRateList();

      setRateData(res.data?.data || []);
    } catch (err) {
      console.error(
        "Failed to load labour rate list:",
        err
      );

      setRateData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rptRateList();
  }, []);

  // =========================================================
  // SAFE STRING
  // =========================================================
  const safeString = (value) => {
    return String(value ?? "").trim();
  };

  // =========================================================
  // DATE FORMAT
  // =========================================================
  const formatDate = (date) => {
    if (!date) return "";

    const formatted = dayjs(date).format(
      "DD-MM-YYYY"
    );

    return formatted === "Invalid Date"
      ? ""
      : formatted;
  };

  // =========================================================
  // CLEAN DATA
  // =========================================================
  const cleanData = useMemo(() => {
    return rateData.map((item) => ({
      ShopNumber: safeString(item.ShopNumber),

      High_Skilled: safeString(
        item.High_Skilled
      ),

      Skilled: safeString(
        item.Skilled
      ),

      Semi_Skilled: safeString(
        item.Semi_Skilled
      ),

      Unskilled: safeString(
        item.Unskilled
      ),

      Labour_On_Cost: safeString(
        item.Labour_On_Cost
      ),

      Store_On_Cost: safeString(
        item.Store_On_Cost
      ),

      RateUpdateDate: formatDate(
        safeString(item.RateUpdateDate)
      ),
    }));
  }, [rateData]);

  // =========================================================
  // EXCEL DOWNLOAD
  // =========================================================
  const downloadExcel = () => {
    if (!cleanData.length) {
      alert(
        "No labour rate records available."
      );
      return;
    }

    const data = cleanData.map((item) => ({
      "Shop Number": item.ShopNumber,
      "High Skilled": item.High_Skilled,
      Skilled: item.Skilled,
      "Semi Skilled": item.Semi_Skilled,
      Unskilled: item.Unskilled,
      "Labour On Cost": item.Labour_On_Cost,
      "Store On Cost": item.Store_On_Cost,
      "Rate Updated On": item.RateUpdateDate,
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Labour Rate List"
    );

    // Excel column widths
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob(
      [excelBuffer],
      {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );

    saveAs(
      file,
      "Labour_Rate_List.xlsx"
    );
  };

  // =========================================================
  // PDF DOWNLOAD
  // =========================================================
  const downloadPDF = () => {
    if (!cleanData.length) {
      alert(
        "No labour rate records available."
      );
      return;
    }

    generateReportPDF({
      title: "Labour Rate List Report",

      fileName: "Labour_Rate_List",

      columns: [
        "S.No",
        "Shop Number",
        "High Skilled",
        "Skilled",
        "Semi Skilled",
        "Unskilled",
        "Labour On Cost",
        "Store On Cost",
        "Rate Updated On",
      ],

      rows: cleanData.map(
        (item, index) => [
          index + 1,
          item.ShopNumber,
          item.High_Skilled,
          item.Skilled,
          item.Semi_Skilled,
          item.Unskilled,
          item.Labour_On_Cost,
          item.Store_On_Cost,
          item.RateUpdateDate,
        ]
      ),
    });
  };

  // =========================================================
  // DATAGRID ROWS
  // =========================================================
  const rows = useMemo(() => {
    return cleanData.map((item, index) => ({
      id: index + 1,
      serialNo: index + 1,
      ShopNumber: item.ShopNumber,
      High_Skilled: item.High_Skilled,
      Skilled: item.Skilled,
      Semi_Skilled: item.Semi_Skilled,
      Unskilled: item.Unskilled,
      Labour_On_Cost: item.Labour_On_Cost,
      Store_On_Cost: item.Store_On_Cost,
      RateUpdateDate: item.RateUpdateDate,
    }));
  }, [cleanData]);

  // =========================================================
  // DATAGRID COLUMNS
  // =========================================================
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      width: 75,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "ShopNumber",
      headerName: "Shop Number",
      width: 140,
    },

    {
      field: "High_Skilled",
      headerName: "High Skilled",
      width: 150,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "Skilled",
      headerName: "Skilled",
      width: 140,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "Semi_Skilled",
      headerName: "Semi Skilled",
      width: 155,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "Unskilled",
      headerName: "Unskilled",
      width: 140,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "Labour_On_Cost",
      headerName: "Labour On Cost",
      width: 165,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "Store_On_Cost",
      headerName: "Store On Cost",
      width: 165,
      headerAlign: "right",
      align: "right",
    },

    {
      field: "RateUpdateDate",
      headerName: "Rate Updated On",
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <Box>
      {/* =====================================================
          BREADCRUMBS
      ===================================================== */}
      <BreadcrumbsBar
        page="Labour Rate List"
      />

      {/* =====================================================
          PROFESSIONAL HEADER
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background */}
        <Box
          sx={{
            position: "absolute",
            right: -50,
            top: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <PriceChangeIcon
              sx={{
                fontSize: 34,
              }}
            />
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Labour Rate List
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 680,
              }}
            >
              View, search and download the current
              labour rates maintained for all shops.
            </Typography>
          </Box>

          {/* Record Count */}
          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 180,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Total Rates
            </Typography>

            <Typography
              variant="h5"
              color="primary"
              fontWeight={800}
              sx={{
                mt: 0.2,
              }}
            >
              {cleanData.length.toLocaleString()}
            </Typography>

            <Chip
              label={
                cleanData.length
                  ? "Data Available"
                  : "No Records"
              }
              size="small"
              color={
                cleanData.length
                  ? "success"
                  : "default"
              }
              variant="outlined"
              sx={{
                mt: 0.5,
                fontWeight: 600,
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* =====================================================
          EXPORT ACTIONS
      ===================================================== */}
      <Paper
        elevation={0}
        className="no-print"
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <DownloadIcon
              color="primary"
              sx={{
                fontSize: 26,
              }}
            />

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Export Rate List
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Download the complete rate list in
                Excel or PDF format.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadExcel}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download Excel
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={
                <PictureAsPdfIcon />
              }
              onClick={downloadPDF}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download PDF
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={rptRateList}
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 130,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* =====================================================
          RATE DATA TABLE
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Labour Rate Records
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Search, filter and review shop-wise
                labour rates.
              </Typography>
            </Box>

            <Chip
              icon={<PriceChangeIcon />}
              label={`${cleanData.length.toLocaleString()} Rates`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>

        <Divider />

        {/* DataGrid */}
        <Box
          sx={{
            height: {
              xs: 540,
              md: 650,
            },
            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[
              10,
              25,
              50,
              100,
            ]}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  debounceMs: 500,
                },
              },
            }}
            sx={{
  border: 0,

  /* ================================
     COLUMN HEADER
     ================================ */
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#ffffff !important",
    fontWeight: 700,
  },

 

  /* Menu / filter icon */
  "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
    color: "#ffffff !important",
  },

  /* ================================
     TABLE CELLS
     ================================ */
  "& .MuiDataGrid-cell": {
    borderColor: "divider",
  },

  /* ================================
     ROW HOVER
     ================================ */
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(30, 3, 236, 0.04)",
  },

  /* ================================
     TOOLBAR
     ================================ */
  "& .MuiDataGrid-toolbarContainer": {
    padding: "12px 16px",
    borderBottom: "1px solid",
    borderColor: "divider",
  },

  /* ================================
     FOOTER
     ================================ */
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid",
    borderColor: "divider",
  },
}}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default RateList;

